import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';

const API = process.env.REACT_APP_BACKEND_URL || '';
const REFRESH_MS = 30000;

// ---------- Asset registry ----------
// dual: an Indian stock co-listed on both NSE (.NS) and BSE (.BO) — two
// independent order books that can quote slightly different prices, shown
// side-by-side. derived: a cross-rate computed from two other symbols.
const dualListed = (section, name, root) => ({
  section, name, tag: 'BSE · NSE', dual: true,
  symbolNSE: `${root}.NS`, symbolBSE: `${root}.BO`,
});

const ASSETS = [
  // Aquaculture / Seafood (India)
  dualListed('Aquaculture / Seafood (India)', 'Sharat Industries Ltd', 'SHINDL'),
  dualListed('Aquaculture / Seafood (India)', 'Avanti Feeds Ltd', 'AVANTIFEED'),
  dualListed('Aquaculture / Seafood (India)', 'Apex Frozen Foods', 'APEX'),
  dualListed('Aquaculture / Seafood (India)', 'Coastal Corporation Ltd', 'COASTCORP'),
  { section: 'Aquaculture / Seafood (India)', name: 'Kings Infra Ventures Ltd', tag: 'BSE', symbol: 'KINGSINFR.BO', href: 'https://www.screener.in/company/530215/' },
  dualListed('Aquaculture / Seafood (India)', 'Waterbase Ltd', 'WATERBASE'),
  { section: 'Aquaculture / Seafood (India)', name: 'Zeal Aqua', tag: 'BSE', symbol: 'ZEAL.BO', href: 'https://www.screener.in/company/539963/' },
  { section: 'Aquaculture / Seafood (India)', name: 'Essex Marine', tag: 'BSE SME', symbol: 'ESSEX.BO', href: 'https://www.screener.in/company/544475/' },

  // Other Indian Stocks & Indices
  { section: 'Other Indian Stocks & Indices', name: 'Sensex', tag: 'BSE index', symbol: '^BSESN' },
  { section: 'Other Indian Stocks & Indices', name: 'NIFTY 50', tag: 'NSE index', symbol: '^NSEI' },
  dualListed('Other Indian Stocks & Indices', 'Bhagyanagar India Ltd', 'BHAGYANGR'),
  dualListed('Other Indian Stocks & Indices', 'Commercial Syn Bags Ltd', 'COMSYN'),
  dualListed('Other Indian Stocks & Indices', 'Motisons Jewellers Ltd', 'MOTISONS'),
  dualListed('Other Indian Stocks & Indices', 'Shanti Gold International Ltd', 'SHANTIGOLD'),
  dualListed('Other Indian Stocks & Indices', 'Ratnaveer Precision Engineering Ltd', 'RATNAVEER'),
  { section: 'Other Indian Stocks & Indices', name: 'Tejassvi Aaharam Ltd', tag: 'BSE', symbol: 'TEJASSVI.BO', href: 'https://www.screener.in/company/531628/' },
  dualListed('Other Indian Stocks & Indices', 'BGR Energy Systems Ltd', 'BGRENERGY'),
  dualListed('Other Indian Stocks & Indices', 'Lenskart Solutions Ltd', 'LENSKART'),
  dualListed('Other Indian Stocks & Indices', 'Eternal Ltd', 'ETERNAL'),
  dualListed('Other Indian Stocks & Indices', 'Swiggy Ltd', 'SWIGGY'),
  dualListed('Other Indian Stocks & Indices', 'HDFC Bank Ltd', 'HDFCBANK'),
  dualListed('Other Indian Stocks & Indices', 'Axis Bank Ltd', 'AXISBANK'),
  dualListed('Other Indian Stocks & Indices', 'Shriram Finance Ltd', 'SHRIRAMFIN'),
  dualListed('Other Indian Stocks & Indices', 'Reliance Industries Ltd', 'RELIANCE'),
  dualListed('Other Indian Stocks & Indices', 'Adani Enterprises Ltd', 'ADANIENT'),

  // USA Stocks & Indices
  { section: 'USA Stocks & Indices', name: 'SpaceX (Space Exploration Technologies Corp)', tag: 'NASDAQ: SPCX', symbol: 'SPCX', href: 'https://www.google.com/finance/beta/quote/SPCX:NASDAQ' },
  { section: 'USA Stocks & Indices', name: 'Sandisk Corp', tag: 'NASDAQ: SNDK', symbol: 'SNDK' },
  { section: 'USA Stocks & Indices', name: 'Micron Technology Inc', tag: 'NASDAQ: MU', symbol: 'MU' },
  { section: 'USA Stocks & Indices', name: 'NVIDIA Corp', tag: 'NASDAQ: NVDA', symbol: 'NVDA' },
  { section: 'USA Stocks & Indices', name: 'Apple', tag: 'NASDAQ: AAPL', symbol: 'AAPL' },
  { section: 'USA Stocks & Indices', name: 'Google (Alphabet)', tag: 'NASDAQ: GOOGL', symbol: 'GOOGL' },
  { section: 'USA Stocks & Indices', name: 'Microsoft', tag: 'NASDAQ: MSFT', symbol: 'MSFT' },
  { section: 'USA Stocks & Indices', name: 'Nasdaq Composite', tag: 'Index', symbol: '^IXIC' },
  { section: 'USA Stocks & Indices', name: 'Dow Jones Industrial Average', tag: 'Index', symbol: '^DJI' },
  { section: 'USA Stocks & Indices', name: 'Tesla Inc', tag: 'NASDAQ: TSLA', symbol: 'TSLA' },

  // Other Global Indices
  { section: 'Other Global Indices', name: 'KOSPI Index', tag: 'South Korea', symbol: '^KS11' },
  { section: 'Other Global Indices', name: 'Nikkei 225', tag: 'Japan', symbol: '^N225' },

  // Forex
  { section: 'Forex', name: 'USD to INR', tag: 'Forex', symbol: 'INR=X', href: 'https://www.google.com/finance/quote/USD-INR' },
  { section: 'Forex', name: 'Russian Ruble to INR', tag: 'Forex · derived', derived: 'RUBINR', href: 'https://www.google.com/finance/quote/RUB-INR' },
  { section: 'Forex', name: 'EUR to INR', tag: 'Forex', symbol: 'EURINR=X', href: 'https://www.google.com/finance/quote/EUR-INR' },
  { section: 'Forex', name: 'USD to Russian Ruble', tag: 'Forex', symbol: 'RUB=X', href: 'https://www.google.com/finance/quote/USD-RUB' },
  { section: 'Forex', name: 'UK Pound to INR', tag: 'Forex', symbol: 'GBPINR=X', href: 'https://www.google.com/finance/quote/GBP-INR' },

  // Commodities
  { section: 'Commodities', name: 'Gold', tag: 'COMEX · USD/oz', symbol: 'GC=F', href: 'https://www.google.com/finance/quote/GCW00:COMEX' },
  { section: 'Commodities', name: 'Silver', tag: 'COMEX · USD/oz', symbol: 'SI=F', href: 'https://www.google.com/finance/quote/SIW00:COMEX' },
  { section: 'Commodities', name: 'Crude Oil (WTI)', tag: 'NYMEX · USD/bbl', symbol: 'CL=F', href: 'https://www.google.com/finance/quote/CLW00:NYMEX' },
];

const ALL_SYMBOLS = Array.from(new Set(
  ASSETS.flatMap(a => a.dual ? [a.symbolNSE, a.symbolBSE] : (a.symbol ? [a.symbol] : []))
));

const SECTIONS = Array.from(new Set(ASSETS.map(a => a.section)));

const CURRENCY_SIGIL = { INR: '₹', USD: '$', EUR: '€', GBP: '£' };
const formatPrice = (value, currency) => {
  if (value == null || isNaN(value)) return '—';
  const sigil = CURRENCY_SIGIL[currency] || '';
  const decimals = value >= 1000 ? 2 : (value >= 1 ? 2 : 4);
  return sigil + Number(value).toLocaleString(undefined, {
    minimumFractionDigits: decimals, maximumFractionDigits: decimals,
  });
};
const formatTime = (ts) => ts ? new Date(ts).toLocaleTimeString() : '';

// RUB→INR = (USD/INR) / (USD/RUB).
const derive = (key, quotes) => {
  if (key !== 'RUBINR') return null;
  const usdinr = quotes['INR=X'];
  const usdrub = quotes['RUB=X'];
  if (!usdinr?.price || !usdrub?.price) return null;
  return {
    price: usdinr.price / usdrub.price,
    prevClose: (usdinr.prevClose && usdrub.prevClose) ? (usdinr.prevClose / usdrub.prevClose) : null,
    currency: 'INR',
    ts: Math.max(usdinr.ts || 0, usdrub.ts || 0),
  };
};

// ---------- Deep-link helper ----------
// Indian equities (co-listed dual + BSE-only single) -> Screener.in.
// Global / US equities & indices -> Google Finance. Indices need explicit
// exchange codes because Yahoo's `^XXX` symbol won't resolve on Google.
// Sensex + NIFTY 50 are intentionally unlinked (source dashboard leaves
// them plain). Forex & commodities carry explicit `href` overrides.
const INDEX_GOOGLE_MAP = {
  '^BSESN': 'SENSEX:INDEXBOM',
  '^NSEI':  'NIFTY_50:INDEXNSE',
  '^IXIC':  '.IXIC:INDEXNASDAQ',
  '^DJI':   '.DJI:INDEXDJX',
  '^KS11':  'KOSPI:KRX',
  '^N225':  'NI225:INDEXNIKKEI',
};
const stripIndianSuffix = (sym) => sym.replace(/\.(NS|BO)$/, '');
const getAssetHref = (asset) => {
  if (asset.href) return asset.href;                                    // explicit override
  if (asset.derived) return null;                                       // derived cross-rates
  if (asset.dual) {
    return `https://www.screener.in/company/${stripIndianSuffix(asset.symbolNSE)}/`;
  }
  const s = asset.symbol;
  if (!s) return null;
  if (s.endsWith('.NS') || s.endsWith('.BO')) {
    return `https://www.screener.in/company/${stripIndianSuffix(s)}/`;
  }
  if (s.startsWith('^')) {
    const q = INDEX_GOOGLE_MAP[s];
    return q ? `https://www.google.com/finance/quote/${q}` : null;
  }
  // Forex / commodities have "=X" or "=F" — no auto link (use explicit href).
  if (s.includes('=')) return null;
  // Remaining plain uppercase tickers are US equities on NASDAQ.
  return `https://www.google.com/finance/quote/${s}:NASDAQ`;
};

// Renders the asset name as a hyperlink when a deep-link URL exists.
const AssetName = ({ asset }) => {
  const href = getAssetHref(asset);
  if (!href) return <span className="name">{asset.name}</span>;
  return (
    <a
      className="name asset-link"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`asset-link-${asset.name}`}
    >
      {asset.name}
    </a>
  );
};

// ---------- Presentational bits ----------
// Renders one price+delta value-row from a quote entry.
const ValueColumn = ({ label, data }) => {
  const priceEl = data && data.price != null && !data.error
    ? <span className="price">{formatPrice(data.price, data.currency)}</span>
    : <span className="price">{data && data.error ? 'No data' : '…'}</span>;

  let deltaEl = <span className="delta flat"></span>;
  if (data && data.price != null && data.prevClose != null) {
    const change = data.price - data.prevClose;
    const pct = data.prevClose ? (change / data.prevClose) * 100 : 0;
    const sign = change > 0 ? '+' : '';
    const cls = change > 0 ? 'up' : change < 0 ? 'down' : 'flat';
    deltaEl = (
      <span className={`delta ${cls}`}>
        {sign}{change.toFixed(2)} ({sign}{pct.toFixed(2)}%)
      </span>
    );
  }

  return (
    <div className="value-row">
      {label && <span className="ex-label">{label}</span>}
      {priceEl}
      {deltaEl}
    </div>
  );
};

const Tile = ({ asset, quotes, sectionHasDual }) => {
  // SpaceX-style: no exchange listing at all — render as a plain tile with
  // just the (hyperlinked) name and a dash. No "Not publicly traded" callout.
  if (asset.symbol === null && !asset.dual && !asset.derived) {
    return (
      <div className="tile not-listed">
        <div className="row-id">
          <AssetName asset={asset} />
          {asset.tag && <span className="tag">{asset.tag}</span>}
        </div>
        <span className="price">—</span>
        <div className="meta"></div>
      </div>
    );
  }

  // Dual (NSE + BSE) row
  if (asset.dual) {
    const nseData = quotes[asset.symbolNSE];
    const bseData = quotes[asset.symbolBSE];
    const anyLive = (nseData && nseData.price != null) || (bseData && bseData.price != null);
    const metaSource =
      (nseData?.ts && (!bseData?.ts || nseData.ts >= bseData.ts)) ? nseData
      : (bseData?.ts ? bseData : (nseData || bseData));
    return (
      <div className={`tile dual${anyLive ? '' : ' unavailable'}`}>
        <div className="row-id">
          <AssetName asset={asset} />
          <span className="tag">{asset.tag}</span>
        </div>
        <ValueColumn label="NSE" data={nseData} />
        <ValueColumn label="BSE" data={bseData} />
        <div className="meta">{metaSource?.ts ? `Updated ${formatTime(metaSource.ts)}` : 'Loading…'}</div>
      </div>
    );
  }

  // Single / derived row
  const data = asset.derived ? derive(asset.derived, quotes) : quotes[asset.symbol];
  const live = data && data.price != null;
  return (
    <div className={`tile${live ? '' : ' unavailable'}`}>
      <div className="row-id">
        <AssetName asset={asset} />
        <span className="tag">{asset.tag}</span>
      </div>
      <ValueColumn data={data} />
      {sectionHasDual && <div className="value-row empty"><span className="price"></span><span className="delta flat"></span></div>}
      <div className="meta">{data?.ts ? `Updated ${formatTime(data.ts)}` : 'Loading…'}</div>
    </div>
  );
};

// ---------- Page ----------
const MarketWatchPage = () => {
  const [quotes, setQuotes] = useState({});
  const [lastUpdated, setLastUpdated] = useState(null);
  const [busy, setBusy] = useState(false);
  const [query, setQuery] = useState('');
  const [countdown, setCountdown] = useState(REFRESH_MS / 1000);
  const abortRef = useRef(null);

  const refresh = useCallback(async () => {
    setBusy(true);
    try {
      if (abortRef.current) abortRef.current.abort();
      abortRef.current = new AbortController();
      const url = `${API}/api/quotes?symbols=${encodeURIComponent(ALL_SYMBOLS.join(','))}`;
      const res = await fetch(url, { cache: 'no-store', signal: abortRef.current.signal });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      const now = Date.now();
      const next = {};
      Object.entries(json.quotes || {}).forEach(([sym, q]) => {
        if (q && !q.error && q.price != null && q.prevClose != null) {
          next[sym] = { price: q.price, prevClose: q.prevClose, currency: q.currency || '', ts: now };
        } else {
          next[sym] = { error: q?.error || 'no data', ts: now };
        }
      });
      setQuotes(next);
      setLastUpdated(now);
    } catch (e) {
      if (e.name !== 'AbortError') console.error('market-watch refresh failed', e);
    } finally {
      setBusy(false);
      setCountdown(REFRESH_MS / 1000);
    }
  }, []);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, REFRESH_MS);
    return () => { clearInterval(id); if (abortRef.current) abortRef.current.abort(); };
  }, [refresh]);

  useEffect(() => {
    const id = setInterval(() => setCountdown((c) => (c > 0 ? c - 1 : 0)), 1000);
    return () => clearInterval(id);
  }, []);

  const q = query.trim().toLowerCase();
  const bySection = useMemo(() => {
    const map = {};
    SECTIONS.forEach(s => { map[s] = ASSETS.filter(a => a.section === s && (!q || a.name.toLowerCase().includes(q))); });
    return map;
  }, [q]);

  const statusText = lastUpdated
    ? `Updated ${formatTime(lastUpdated)} · next in ${countdown}s`
    : 'Loading…';

  return (
    <>
      <style>{DASHBOARD_CSS}</style>
      <div className="mw-root" data-testid="market-watch-page">
        <header>
          <div className="header-row">
            <div>
              <h1>Daily Market Dashboard</h1>
              <div className="subtitle">Indian &amp; US equities, indices, forex, and commodities · auto-refreshes every 30 seconds</div>
            </div>
            <div className="controls">
              <input
                type="text"
                id="search"
                placeholder="Filter by name..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                data-testid="market-watch-search"
              />
              <span className="status-pill">
                <span className={`dot${busy ? ' busy' : ''}`}></span>
                <span>{statusText}</span>
              </span>
              <button id="refreshBtn" onClick={refresh} disabled={busy} data-testid="market-watch-refresh">
                Refresh now
              </button>
            </div>
          </div>
          <div className="legend">
            <span><span className="swatch" style={{ background: 'var(--up)' }}></span>Price up since previous close</span>
            <span><span className="swatch" style={{ background: 'var(--down)' }}></span>Price down since previous close</span>
            <span><span className="swatch" style={{ background: 'var(--text-muted)' }}></span>No live data available</span>
          </div>
        </header>

        <main>
          {SECTIONS.map((s) => {
            const rows = bySection[s];
            if (!rows.length) return null;
            const sectionHasDual = ASSETS.some(a => a.section === s && a.dual);
            return (
              <div className="section" key={s}>
                <h2>{s}</h2>
                <div className={`grid${sectionHasDual ? ' has-dual' : ''}`}>
                  {rows.map((asset) => (
                    <Tile key={asset.name} asset={asset} quotes={quotes} sectionHasDual={sectionHasDual} />
                  ))}
                </div>
              </div>
            );
          })}
        </main>
      </div>
    </>
  );
};

// Verbatim CSS from the source HTML, scoped under .mw-root so it doesn't leak
// into the rest of the app. Keeping it inline (rather than a separate .css
// file) means the whole dashboard is one self-contained React unit.
const DASHBOARD_CSS = `
.mw-root {
  --page:        #f9f9f7;
  --surface:     #fcfcfb;
  --text-1:      #0b0b0b;
  --text-2:      #52514e;
  --text-muted:  #898781;
  --grid:        #e1e0d9;
  --baseline:    #c3c2b7;
  --border:      rgba(11,11,11,0.10);
  --up:          #006300;
  --down:        #d03b3b;
  --warn-bg:     #fff6e5;
  --warn-text:   #8a5a00;
  --accent:      #2a78d6;
  background: var(--page);
  color: var(--text-1);
  font-family: system-ui, -apple-system, "Segoe UI", sans-serif;
  min-height: 100vh;
}
@media (prefers-color-scheme: dark) {
  .mw-root {
    --page:        #0d0d0d;
    --surface:     #1a1a19;
    --text-1:      #ffffff;
    --text-2:      #c3c2b7;
    --text-muted:  #898781;
    --grid:        #2c2c2a;
    --baseline:    #383835;
    --border:      rgba(255,255,255,0.10);
    --up:          #0ca30c;
    --down:        #e66767;
    --warn-bg:     #2a2410;
    --warn-text:   #e8c874;
    --accent:      #3987e5;
  }
}
.mw-root * { box-sizing: border-box; }
.mw-root header {
  position: sticky; top: 0; z-index: 5;
  background: var(--page);
  border-bottom: 1px solid var(--border);
  padding: 14px 20px 12px;
}
.mw-root .header-row {
  display: flex; align-items: center; justify-content: space-between;
  gap: 12px; flex-wrap: wrap;
}
.mw-root h1 {
  font-size: 19px; font-weight: 650; margin: 0;
  letter-spacing: -0.01em;
}
.mw-root .subtitle { font-size: 12.5px; color: var(--text-muted); margin-top: 2px; }
.mw-root .controls { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.mw-root .status-pill {
  font-size: 12px; color: var(--text-2);
  display: flex; align-items: center; gap: 6px;
  background: var(--surface); border: 1px solid var(--border);
  border-radius: 999px; padding: 5px 12px;
}
.mw-root .dot { width: 7px; height: 7px; border-radius: 50%; background: var(--up); flex: none; }
.mw-root .dot.busy { background: var(--accent); animation: mw-pulse 1s infinite ease-in-out; }
@keyframes mw-pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.35; } }
.mw-root button#refreshBtn {
  font-size: 12.5px; font-weight: 600;
  background: var(--accent); color: #fff;
  border: none; border-radius: 8px;
  padding: 7px 14px; cursor: pointer;
}
.mw-root button#refreshBtn:active { transform: translateY(1px); }
.mw-root button#refreshBtn:disabled { opacity: 0.6; cursor: default; }
.mw-root input#search {
  font-size: 13px; padding: 7px 12px;
  border-radius: 8px; border: 1px solid var(--border);
  background: var(--surface); color: var(--text-1);
  width: 220px; max-width: 45vw;
}
.mw-root input#search::placeholder { color: var(--text-muted); }
.mw-root .legend {
  display: flex; gap: 14px; font-size: 12px; color: var(--text-2);
  margin-top: 10px; flex-wrap: wrap;
}
.mw-root .legend span { display: inline-flex; align-items: center; gap: 5px; }
.mw-root .swatch { width: 10px; height: 10px; border-radius: 3px; flex: none; }
.mw-root main { padding: 6px 20px 40px; max-width: 1280px; margin: 0 auto; }
.mw-root .section { margin-top: 26px; }
.mw-root .section h2 {
  font-size: 13px; text-transform: uppercase; letter-spacing: 0.04em;
  color: var(--text-muted); font-weight: 650;
  margin: 0 0 10px 2px;
}
.mw-root .grid {
  display: flex; flex-direction: column;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 12px;
  overflow: hidden;
}
.mw-root .tile {
  display: grid;
  grid-template-columns: minmax(110px, 1fr) 280px 150px;
  align-items: center;
  column-gap: 14px;
  padding: 9px 16px;
  min-height: 40px;
  border-bottom: 1px solid var(--grid);
}
.mw-root .grid.has-dual .tile {
  grid-template-columns: minmax(110px, 1fr) 310px 310px 140px;
}
.mw-root .tile:last-child { border-bottom: none; }
.mw-root .tile:hover { background: var(--page); }
.mw-root .tile .row-id {
  min-width: 0; overflow: hidden;
  display: flex; align-items: baseline; gap: 9px;
}
.mw-root .tile .name {
  font-size: 13px; font-weight: 600; line-height: 1.3;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.mw-root .tile .name.asset-link {
  color: var(--accent);
  text-decoration: none;
  cursor: pointer;
}
.mw-root .tile .name.asset-link:hover { text-decoration: underline; }
.mw-root .tile .name.asset-link:visited { color: var(--accent); }
.mw-root .tile .tag {
  font-size: 10.5px; color: var(--text-muted);
  white-space: nowrap; flex: none;
}
.mw-root .tile .value-row {
  display: flex; align-items: baseline; justify-content: flex-end;
  gap: 6px; min-width: 0;
}
.mw-root .tile .value-row.empty { visibility: hidden; }
.mw-root .tile .ex-label {
  font-size: 10px; font-weight: 700; color: var(--text-muted);
  letter-spacing: 0.02em; flex: none; width: 24px;
}
.mw-root .tile .price {
  font-size: 14px; font-weight: 650; letter-spacing: -0.01em;
  font-variant-numeric: tabular-nums;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  flex: none; width: 110px; text-align: right;
}
.mw-root .tile .delta {
  font-size: 11.5px; font-weight: 600; white-space: nowrap;
  font-variant-numeric: tabular-nums;
  flex: none; text-align: left; overflow: hidden; text-overflow: ellipsis;
  width: 150px; margin-left: 10px;
}
.mw-root .tile .delta.up { color: var(--up); }
.mw-root .tile .delta.down { color: var(--down); }
.mw-root .tile .delta.flat { color: var(--text-muted); }
.mw-root .tile .meta {
  font-size: 10.5px; color: var(--text-muted);
  text-align: right;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.mw-root .tile .meta.stale { color: var(--warn-text); }
.mw-root .tile.unavailable { opacity: 0.6; }
.mw-root .tile.unavailable .price { font-size: 12.5px; color: var(--text-muted); font-weight: 600; }
.mw-root .tile.not-listed .delta { display: none; }
.mw-root .tile.not-listed .price {
  width: auto; max-width: 100%; text-align: left;
  overflow: visible; text-overflow: clip;
}
@media (max-width: 990px) {
  .mw-root .tile .tag { display: none; }
  .mw-root .tile { grid-template-columns: minmax(90px, 1fr) 253px 110px; }
  .mw-root .grid.has-dual .tile { grid-template-columns: minmax(90px, 1fr) 283px 283px 100px; }
  .mw-root .tile .price { width: 95px; }
  .mw-root .tile .delta { width: 138px; margin-left: 8px; }
}
@media (max-width: 870px) {
  .mw-root .tile {
    display: flex; flex-direction: column; align-items: flex-start;
    row-gap: 3px; min-height: 0;
  }
  .mw-root .tile .row-id { width: 100%; }
  .mw-root .tile .value-row { width: 100%; justify-content: flex-start; }
  .mw-root .tile .price { width: auto; text-align: left; }
  .mw-root .tile .delta { width: auto; margin-left: 6px; }
  .mw-root .tile .meta { width: 100%; text-align: left; }
}
`;

export default MarketWatchPage;
